import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect, type SelectOption, SyutTreeSelect, type TreeSelectOption } from 'components/ui';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { accountTypeService, reportClassService, accountSubtypeService, coaGroupService } from 'api/services';
import type { CoaGroupDto, AccountSubtypeDto } from 'api/types';

/** A group code in the structured form (ADR-0061 rev 3): subtype block + 2-digit serial, e.g. 130101. */
const STRUCTURED_GROUP_CODE = /^[0-9]{6}$/;

// ============================================================================
// ACCOUNT GROUP MODALS — reference-parity with SampleModuleModal
// Form modal (create / edit / copy + minimize + concurrency) and View modal.
// Modal talks to coaGroupService directly (same as the sample). Entity-scoped
// via the X-Entity-ID header.
// ============================================================================

export interface AccountGroupFormData {
  accountTypeId: string;
  accountSubtypeId: string;
  parentGroupId: string;
  groupCode: string;
  groupName: string;
  displayOrder: string;     // string for input; parsed to number on save
  description: string;
  statusCode: string;
}

const INITIAL_FORM_DATA: AccountGroupFormData = {
  accountTypeId: '',
  accountSubtypeId: '',
  parentGroupId: '',
  groupCode: '',
  groupName: '',
  displayOrder: '0',
  description: '',
  statusCode: 'ACTIVE',
};

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

function buildFormData(group: CoaGroupDto | null | undefined, mode: 'create' | 'edit' | 'copy'): AccountGroupFormData {
  if ((mode === 'edit' || mode === 'copy') && group) {
    return {
      accountTypeId: group.accountTypeId,
      accountSubtypeId: group.accountSubtypeId,
      parentGroupId: group.parentGroupId ?? '',
      groupCode: mode === 'copy' ? '' : group.groupCode,
      groupName: mode === 'copy' ? `${group.groupName} (Copy)` : group.groupName,
      displayOrder: String(group.displayOrder ?? 0),
      description: group.description ?? '',
      // Empty → backend defaults to ACTIVE via IStatusCache.
      statusCode: mode === 'copy' ? '' : (group.statusCode || ''),
    };
  }
  return { ...INITIAL_FORM_DATA };
}

// ============================================================================
// FORM MODAL
// ============================================================================

export interface AccountGroupFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  /** Record to populate in edit/copy mode. Null/undefined in create mode. */
  group?: CoaGroupDto | null;
  onSave: () => void;
  onClose: () => void;

  openedBy?: string;

  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, groupCode?: string) => void;
  createInstanceId?: string;
  copyInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

export const AccountGroupFormModal: React.FC<AccountGroupFormModalProps> = ({
  isOpen,
  mode,
  group,
  onSave,
  onClose,
  openedBy,
  tabId,
  moduleName,
  onRestoreModal,
  createInstanceId,
  copyInstanceId,
  minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  const [formData, setFormData] = useState<AccountGroupFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const groupRef = useRef(group);
  groupRef.current = group;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const copyInstanceIdRef = useRef(copyInstanceId);
  copyInstanceIdRef.current = copyInstanceId;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);
  const justRestoredRef = useRef(false);   // this open was a restore from the minimize bar

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [concurrencyConflict, setConcurrencyConflict] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  // Populate form on open; skip on restore to preserve user edits
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    justRestoredRef.current = isRestoreRef.current;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const data = buildFormData(groupRef.current, modeRef.current);
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentCopyInstanceId = copyInstanceIdRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else if (currentMode === 'copy') {
      modalId = `${tabId}-form-modal-copy-${currentCopyInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentFormData.groupCode || 'new'}`;
    }

    let title: string;
    const code = currentFormData.groupCode?.trim();
    const name = currentFormData.groupName?.trim();
    if (currentMode === 'create') {
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else if (currentMode === 'copy') {
      const idx = currentCopyInstanceId || '1';
      title = code ? `Copy - ${code} (${idx})` : name ? `Copy - ${name} (${idx})` : `Copy (${idx})`;
    } else {
      title = `Edit - ${currentFormData.groupCode || 'Group'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(currentFormData) !== savedSnapshot : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        onRestoreModal?.(
          currentMode,
          currentMode === 'create' ? currentCreateInstanceId : currentMode === 'copy' ? currentCopyInstanceId : undefined,
          currentMode !== 'create' ? currentFormData.groupCode : undefined,
        );
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const getFieldError = (field: string) => fieldErrors[field];
  const clearFieldError = (field: string) => {
    setFieldErrors(prev => { if (!prev[field]) return prev; const n = { ...prev }; delete n[field]; return n; });
  };

  // ------------------------------------------------------------------
  // Account category tree (Type › Report Class › Subtype), the same picker as the ledger form
  // (ADR-0061 rev 3). A group sits on a SUBTYPE; its type follows from it. Type/class rows are
  // headings only. The subtype list is already narrowed to the entity's country / legal form.
  // ------------------------------------------------------------------
  const [categoryTree, setCategoryTree] = useState<TreeSelectOption[]>([]);
  const [treeLoaded, setTreeLoaded] = useState(false);
  const subtypeMapRef = useRef<Map<string, AccountSubtypeDto>>(new Map());
  const typeNameRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (!isOpen || treeLoaded) return;
    let cancelled = false;
    Promise.all([
      accountTypeService.getAll({ pageSize: 50 }),
      reportClassService.getAll({ pageSize: 100 }),
      accountSubtypeService.getAll({ pageSize: 100 }),
    ]).then(([types, classes, subtypes]) => {
      if (cancelled) return;
      subtypeMapRef.current = new Map(subtypes.data.map(s => [s.id, s]));
      typeNameRef.current = new Map(types.data.map(t => [t.id, t.typeName]));
      const usedClasses = new Set(subtypes.data.map(s => s.reportClassId).filter(Boolean) as string[]);
      const usedTypes = new Set(subtypes.data.map(s => s.accountTypeId));
      setCategoryTree([
        ...types.data.filter(t => usedTypes.has(t.id)).map(t => ({
          value: `t:${t.id}`, label: t.typeName, isCategory: true, sortOrder: t.displayOrder,
        })),
        ...classes.data.filter(rc => usedClasses.has(rc.id)).map(rc => ({
          value: `c:${rc.id}`, label: rc.className, parentValue: `t:${rc.accountTypeId}`, isCategory: true, sortOrder: rc.displayOrder,
        })),
        ...subtypes.data.map(s => ({
          value: s.id,
          label: `${s.subtypeName} (${s.normalBalance})`,
          parentValue: s.reportClassId ? `c:${s.reportClassId}` : `t:${s.accountTypeId}`,
          sortOrder: s.displayOrder,
        })),
      ]);
      setTreeLoaded(true);
    }).catch(() => notificationService.toast.error('Could not load the account categories.'));
    return () => { cancelled = true; };
  }, [isOpen, treeLoaded]);

  // "Asset › Current Assets" under the picker, so the full placement stays visible.
  const categoryPath = useMemo(() => {
    const s = formData.accountSubtypeId ? subtypeMapRef.current.get(formData.accountSubtypeId) : undefined;
    if (!s) return group && formData.accountSubtypeId === group.accountSubtypeId ? group.accountTypeName ?? '' : '';
    return [s.accountTypeName || typeNameRef.current.get(s.accountTypeId), s.reportClassName].filter(Boolean).join(' › ');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.accountSubtypeId, treeLoaded, group]);

  // Open the tree at the current subtype (edit / copy / restore).
  const expandedForValue = useMemo(() => {
    const s = formData.accountSubtypeId ? subtypeMapRef.current.get(formData.accountSubtypeId) : undefined;
    if (!s) return [];
    return [`t:${s.accountTypeId}`, ...(s.reportClassId ? [`c:${s.reportClassId}`] : [])];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeLoaded]);

  // ------------------------------------------------------------------
  // Auto code (ADR-0061 rev 3): the next group code in the subtype's block.
  // Filled only while the code is empty or still the last suggestion, so a
  // code the user typed is never overwritten. Edit mode never re-codes.
  // ------------------------------------------------------------------
  const lastSuggestedRef = useRef('');
  const [suggesting, setSuggesting] = useState(false);
  const [noAutoCode, setNoAutoCode] = useState(false);

  const suggestCode = useCallback(async (subtypeId: string, force = false) => {
    if (modeRef.current === 'edit' || !subtypeId) return;
    const current = formDataRef.current.groupCode;
    if (!force && current && current !== lastSuggestedRef.current) return;
    setSuggesting(true);
    try {
      const code = await coaGroupService.getNextCode(subtypeId);
      if (formDataRef.current.accountSubtypeId !== subtypeId) return;   // user moved on
      setNoAutoCode(!code);
      const next = code ?? '';
      setFormData(prev => {
        if (!force && prev.groupCode && prev.groupCode !== lastSuggestedRef.current) return prev;
        return { ...prev, groupCode: next };
      });
      lastSuggestedRef.current = next;
      if (code) clearFieldError('groupCode');
    } catch {
      /* no suggestion — the user can type a code, or leave it blank for the server to assign */
    } finally {
      setSuggesting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Copy opens with the source's subtype and an empty code — suggest straight away.
  useEffect(() => {
    if (!isOpen) return;
    lastSuggestedRef.current = '';
    setNoAutoCode(false);
    if (justRestoredRef.current || modeRef.current !== 'copy') return;
    const fd = buildFormData(groupRef.current, 'copy');
    if (fd.accountSubtypeId) suggestCode(fd.accountSubtypeId, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleCategoryChange = useCallback((val: string | string[] | null) => {
    clearFieldError('accountSubtypeId');
    const v = (Array.isArray(val) ? val[0] : val) || '';
    const s = v ? subtypeMapRef.current.get(v) : undefined;
    const prevData = formDataRef.current;
    // A new category means a new code block: unless the user typed a free-form code, the old
    // code is dropped at once so a group can't be saved with another category's number.
    const recode = modeRef.current !== 'edit' && prevData.accountSubtypeId !== v
      && (!prevData.groupCode || prevData.groupCode === lastSuggestedRef.current || STRUCTURED_GROUP_CODE.test(prevData.groupCode));
    const next = {
      ...prevData,
      accountSubtypeId: v,
      accountTypeId: s?.accountTypeId ?? '',
      parentGroupId: prevData.accountSubtypeId === v ? prevData.parentGroupId : '',
      groupCode: recode ? '' : prevData.groupCode,
    };
    setFormData(next);
    formDataRef.current = next;
    if (recode) {
      lastSuggestedRef.current = '';
      if (v) suggestCode(v, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestCode]);

  const loadParentOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    const subtypeId = formDataRef.current.accountSubtypeId;
    if (!subtypeId) return [];
    const res = await coaGroupService.getAll({ accountSubtypeId: subtypeId, searchTerm: input || undefined, pageSize: 100 });
    return res.data
      .filter(g => g.id !== groupRef.current?.id)
      .map(g => ({ value: g.id, label: `${g.groupCode} - ${g.groupName}` }));
  }, []);

  // Seed options so edit/copy shows labels without a fetch round-trip
  // The saved subtype may sit outside this entity's list (e.g. another country's) — seed its label.
  const subtypeSeed: TreeSelectOption[] | undefined = group ? [{ value: group.accountSubtypeId, label: group.subtypeName }] : undefined;
  const parentSeed = group?.parentGroupId ? [{ value: group.parentGroupId, label: group.parentGroupName ?? '' }] : undefined;

  const handleSave = useCallback(async () => {
    const errors: Record<string, string> = {};
    if (!formData.accountSubtypeId) errors.accountSubtypeId = 'Pick the account category (subtype)';
    // A blank code on create is assigned by the server (next in the subtype's block, ADR-0061 rev 3).
    if (!formData.groupCode.trim() && (mode === 'edit' || noAutoCode)) errors.groupCode = 'Group code is required';
    if (!formData.groupName.trim()) errors.groupName = 'Group name is required';

    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }

    // Async code uniqueness check
    if (formData.groupCode.trim()) {
      try {
        const excludeId = mode === 'edit' ? group?.id : undefined;
        const available = await coaGroupService.isCodeAvailable(formData.groupCode.trim(), excludeId);
        if (!available) {
          setFieldErrors({ groupCode: 'This code already exists. Please use a different code.' });
          return;
        }
      } catch {
        // Validation failed — let server enforce uniqueness
      }
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData, mode, group, noAutoCode]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const payload = {
        accountSubtypeId: formData.accountSubtypeId,
        parentGroupId: formData.parentGroupId || null,
        groupCode: formData.groupCode.trim(),
        groupName: formData.groupName.trim(),
        displayOrder: Number(formData.displayOrder) || 0,
        description: formData.description.trim() || undefined,
        statusCode: formData.statusCode,
      };

      if (mode === 'edit' && group) {
        await coaGroupService.update(group.id, { ...payload, rowVersion: group.rowVersion });
      } else {
        await coaGroupService.create(payload);
      }

      setIsSaving(false);
      const label = mode === 'create' ? 'created' : mode === 'copy' ? 'copied' : 'updated';
      notificationService.toast.success(`Group "${formData.groupName}" has been ${label} successfully.`);
      onSave();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.message;
      if (status === 409 && message) {
        setConcurrencyConflict({ open: true, message });
      } else {
        notificationService.toast.error(message || 'An error occurred while saving. Please try again.');
      }
    }
  }, [formData, mode, group, onSave, onClose]);

  const handleRefreshFromConflict = useCallback(async () => {
    if (!group?.id) { setConcurrencyConflict({ open: false, message: '' }); return; }
    setIsRefreshing(true);
    try {
      const res = await coaGroupService.getById(group.id);
      const fresh = res.data;
      const freshData = buildFormData(fresh, 'edit');
      setFormData(freshData);
      initialSnapshotRef.current = JSON.stringify(freshData);
      setConcurrencyConflict({ open: false, message: '' });
      notificationService.toast.info('Form refreshed with latest data. Please review and save again.');
    } catch {
      notificationService.toast.error('Could not refresh from server. Please close and re-open the record.');
    } finally {
      setIsRefreshing(false);
    }
  }, [group]);

  if (process.env.NODE_ENV === 'development') {
    console.log('AccountGroupFormModal opened', { openedBy: openedBy || 'Unknown', mode });
  }

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {isSaving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem' }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {mode === 'create' ? 'Creating group...' : mode === 'copy' ? 'Copying group...' : 'Saving changes...'}
            </span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'create' && 'Add Account Group'}
            {mode === 'edit' && 'Edit Account Group'}
            {mode === 'copy' && 'Copy Account Group'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <div className="form-grid-2">
            {/* Account Category (Type › Report class › Subtype) */}
            <div className="form-group" data-field="accountSubtypeId">
              <SyutTreeSelect key={treeLoaded ? 'tree' : 'tree-loading'}
                label={<>Account Category <span className="text-danger">*</span></>}
                options={categoryTree} seedOptions={subtypeSeed}
                value={formData.accountSubtypeId || null}
                onChange={handleCategoryChange}
                defaultExpandedValues={expandedForValue}
                placeholder={treeLoaded ? 'Search or pick a subtype…' : 'Loading categories…'}
                isDisabled={!treeLoaded}
                menuPortalTarget={document.body} maxMenuHeight={360}
                showNodeIcons={false} isClearable={false}
                error={getFieldError('accountSubtypeId')}
                helperText={categoryPath || 'Type › Report class › Subtype'} />
            </div>

            {/* Group Code */}
            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <span>Group Code {mode === 'edit' && <span className="text-danger">*</span>}</span>
                {mode !== 'edit' && formData.accountSubtypeId && (
                  <button type="button" className="modal-header-btn" title="Suggest the next code in this category"
                    onClick={() => suggestCode(formData.accountSubtypeId, true)} disabled={suggesting}
                    style={{ padding: '0 0.25rem', height: '1.25rem', marginLeft: 'auto' }}>
                    <IconSystem.refresh size={13} />
                  </button>
                )}
              </label>
              <input
                type="text"
                data-field="groupCode"
                className={`form-input${getFieldError('groupCode') ? ' form-field-error' : ''}`}
                value={formData.groupCode}
                maxLength={30}
                onChange={(e) => { clearFieldError('groupCode'); setFormData(prev => ({ ...prev, groupCode: e.target.value.toUpperCase() })); }}
                placeholder={mode === 'edit' ? '' : suggesting ? 'Finding next code…' : noAutoCode ? 'Enter a code' : 'Auto — from the category'}
                readOnly={mode === 'edit'}
                style={mode === 'edit' ? { background: 'var(--bg-secondary)' } : {}}
              />
              {getFieldError('groupCode')
                ? <div className="form-error-message">{getFieldError('groupCode')}</div>
                : mode !== 'edit' && noAutoCode && formData.accountSubtypeId && (
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
                    No automatic code for this category (its block is full or unnumbered) — enter one.
                  </div>
                )}
            </div>

            {/* Group Name */}
            <div className="form-group">
              <label className="form-label">Group Name <span className="text-danger">*</span></label>
              <input
                type="text"
                data-field="groupName"
                className={`form-input${getFieldError('groupName') ? ' form-field-error' : ''}`}
                value={formData.groupName}
                maxLength={200}
                onChange={(e) => { clearFieldError('groupName'); setFormData(prev => ({ ...prev, groupName: e.target.value })); }}
                placeholder="e.g., Current Assets"
              />
              {getFieldError('groupName') && <div className="form-error-message">{getFieldError('groupName')}</div>}
            </div>

            {/* Parent Group */}
            <div className="form-group" data-field="parentGroupId">
              <SyutSelect
                key={`parent-${formData.accountSubtypeId}`}
                label="Parent Group (optional)"
                isAsync
                loadOptions={loadParentOptions}
                defaultOptions={true}
                cacheOptions={false}
                seedOptions={parentSeed}
                menuPortalTarget={document.body}
                value={formData.parentGroupId}
                onChange={(val) => setFormData(prev => ({ ...prev, parentGroupId: (val as string) || '' }))}
                placeholder={formData.accountSubtypeId ? 'Root group' : 'Select a subtype first'}
                isClearable
              />
            </div>

            {/* Status */}
            <div className="form-group" data-field="statusCode">
              <SyutSelect
                label="Status"
                options={STATUS_OPTIONS}
                menuPortalTarget={document.body}
                value={formData.statusCode}
                onChange={(val) => setFormData(prev => ({ ...prev, statusCode: (val as string) || '' }))}
                isClearable={false}
              />
            </div>

            {/* Display Order */}
            <div className="form-group">
              <label className="form-label">Display Order</label>
              <input
                type="number"
                className="form-input"
                value={formData.displayOrder}
                min={0}
                onChange={(e) => setFormData(prev => ({ ...prev, displayOrder: e.target.value }))}
              />
            </div>
          </div>

          {/* Description */}
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              value={formData.description}
              rows={2}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            <IconSystem.check size={16} />
            {mode === 'edit' ? 'Save Changes' : mode === 'copy' ? 'Create Copy' : 'Create Group'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={
          mode === 'edit'
            ? `Save changes to "${formData.groupName}"?`
            : mode === 'copy'
              ? `Create group as "${formData.groupName}"?`
              : `Create group "${formData.groupName}"?`
        }
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Are you sure you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />

      <ConfirmDialog
        open={concurrencyConflict.open}
        title="Record Modified by Another User"
        message={concurrencyConflict.message || 'This record was modified by another user while your form was open. Refresh to load their changes, or keep your current values.'}
        confirmLabel={isRefreshing ? 'Refreshing...' : 'Refresh from Server'}
        cancelLabel="Keep My Changes"
        variant="default"
        loading={isRefreshing}
        onConfirm={handleRefreshFromConflict}
        onCancel={() => setConcurrencyConflict({ open: false, message: '' })}
      />
    </>
  );
};

// ============================================================================
// VIEW MODAL
// ============================================================================

export interface AccountGroupViewModalProps {
  isOpen: boolean;
  group: CoaGroupDto | null;
  onClose: () => void;
  onEdit?: (group: CoaGroupDto) => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (groupCode: string) => void;
}

const ViewField: React.FC<{ label: string; value?: string | number | null; fullWidth?: boolean }> = ({ label, value, fullWidth }) => (
  <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
    <label className="form-label">{label}</label>
    <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)', fontSize: '0.875rem', minHeight: '36px' }}>
      {value !== null && value !== undefined && value !== '' ? value : <span style={{ color: 'var(--text-muted)' }}>—</span>}
    </div>
  </div>
);

export const AccountGroupViewModal: React.FC<AccountGroupViewModalProps> = ({
  isOpen,
  group,
  onClose,
  onEdit,
  tabId,
  moduleName,
  onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !group) return;
    minimize({
      id: `${tabId}-view-modal-${group.groupCode}`,
      title: `View - ${group.groupCode}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(group.groupCode),
    });
    onClose();
  }, [tabId, moduleName, group, minimize, onClose, onRestoreView]);

  if (!isOpen || !group) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Account Group Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <ViewField label="Code" value={group.groupCode} />
            <div>
              <label className="form-label">Status</label>
              <div style={{ paddingTop: '4px' }}>
                <span className="badge" style={group.statusColor ? { background: group.statusColor, color: '#fff' } : undefined}>
                  {group.statusName || 'Active'}
                </span>
              </div>
            </div>
            <ViewField label="Group Name" value={group.groupName} fullWidth />
            <ViewField label="Description" value={group.description} fullWidth />
            <ViewField label="Account Type" value={group.accountTypeName} />
            <ViewField label="Subtype" value={group.subtypeName} />
            <ViewField label="Parent Group" value={group.parentGroupName} />
            <ViewField label="Display Order" value={group.displayOrder} />
            <ViewField label="Sub-groups" value={group.childCount} />
            <ViewField label="Ledgers" value={group.ledgerCount} />
            <ViewField label="Created By" value={group.createdByName || '—'} />
            <ViewField label="Created At" value={group.createdAt ? new Date(group.createdAt).toLocaleString() : null} />
            {group.updatedAt && (
              <>
                <ViewField label="Updated By" value={group.updatedByName || '—'} />
                <ViewField label="Updated At" value={new Date(group.updatedAt).toLocaleString()} />
              </>
            )}
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => { onClose(); onEdit(group); }}>
              <IconSystem.edit size={16} />
              Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default AccountGroupFormModal;
